// ============================================================
// CENTRAL SITE CONFIGURATION — Rudraksh CS
// ============================================================
// This is the ONE PLACE where all site-wide company information,
// contact details, integration URLs and feature flags live.
//
// EDIT THIS FILE to change:
//   • Company phone, email, address
//   • WhatsApp number (leave "" to hide WhatsApp everywhere)
//   • Google Form URL and product-prefill entry ID
//   • Social media links (leave "" to hide)
//   • Brochure PDF URL (leave "" to hide "Download Brochure")
//   • Google Analytics / Search Console IDs
//   • SEO defaults
//
// Values may also be provided via .env / build-time environment
// variables (see .env.example). Environment variables ALWAYS
// override the defaults below, so this file is safe to commit.
// ============================================================

const env = (typeof process !== "undefined" && process.env) || {};
const pick = (envKey, fallback = "") => (env[envKey] !== undefined && env[envKey] !== "" ? env[envKey] : fallback);

export const company = {
  brandName: "Rudraksh CS",
  fullCompanyName: "Rudraksh Corrugation Solutions",
  legalName: "Rudraksh Corrugation Solutions",
  tagline: "Packaging Solutions Built Around Your Requirements",
  establishedYear: 2009,

  // ---- Contact ------------------------------------------------
  phone: "+91 9810315778",
  phoneHref: "tel:+919810315778",
  email: pick("REACT_APP_COMPANY_EMAIL", "info@rudrakshcs.com"),
  // Leave empty to hide WhatsApp buttons/links across the site.
  // When set, must be digits only, in international format e.g. "919810315778".
  whatsappNumber: pick("REACT_APP_WHATSAPP_NUMBER", ""),

  // ---- Address -----------------------------------------------
  address: {
    line1: "Khasrano 223, Village Sakipur",
    line2: "Sector Zeta-1, Greater Noida",
    region: "Uttar Pradesh",
    postalCode: "201310",
    country: "India",
    countryCode: "IN",
  },
  addressFull:
    "Khasrano 223, Village Sakipur, Sector Zeta-1, Greater Noida, Uttar Pradesh 201310, India",

  // ---- Web presence -------------------------------------------
  website: pick("REACT_APP_SITE_URL", "https://rudrakshcs.com"),
  linkedin: pick("REACT_APP_LINKEDIN_URL", ""),
  instagram: pick("REACT_APP_INSTAGRAM_URL", ""),
  facebook: pick("REACT_APP_FACEBOOK_URL", ""),
  youtube: pick("REACT_APP_YOUTUBE_URL", ""),
  googleBusinessProfile: pick("REACT_APP_GBP_URL", ""),

  // ---- Assets --------------------------------------------------
  logo: {
    // Official Rudraksh CS logo. To swap: replace the file at this path.
    src: "/assets/brand/rudraksh-cs-logo.jpeg",
    alt: "Rudraksh CS",
  },
  brochureUrl: pick("REACT_APP_BROCHURE_URL", ""),
};

// ============================================================
// GOOGLE FORM ENQUIRY CONFIGURATION
// ------------------------------------------------------------
// The website uses a Google Form as its enquiry system.
// There is NO custom backend for enquiries.
//
// To change the form: replace `viewformUrl` below.
// To enable per-product prefill: obtain the "Product" field's
// entry ID (e.g. entry.1234567890) from your Google Form's
// pre-filled link, and paste it in `productEntryId`.
// See CONTENT-MAINTENANCE.md for step-by-step instructions.
// ============================================================
export const googleForm = {
  viewformUrl: pick(
    "REACT_APP_GOOGLE_FORM_URL",
    "https://docs.google.com/forms/d/e/1FAIpQLSe_hYIYkL2nAfFNuWwu0Mg_q9peHFGNR3ZWNvBOGf6ysyxowg/viewform",
  ),
  // Set this to the Google Forms "Product" field entry ID
  // (e.g. "entry.1234567890"). Leave empty to open the form without prefill.
  productEntryId: pick("REACT_APP_GOOGLE_FORM_PRODUCT_ENTRY", "entry.1107514937"),
};

/**
 * Build a URL that opens the Google Form (optionally with the
 * "Product" field pre-selected). Falls back to the plain form URL
 * when productEntryId is not configured.
 *
 * @param {string} [productName]
 * @returns {string}
 */
export function buildQuoteUrl(productName) {
  const base = googleForm.viewformUrl.replace(/\?.*$/, ""); // strip publish-editor params
  const params = new URLSearchParams({ usp: "pp_url" });
  if (productName && googleForm.productEntryId) {
    params.set(googleForm.productEntryId, productName);
  }
  return `${base}?${params.toString()}`;
}

/**
 * Embed variant of the Google Form URL.
 * Uses the same base + swaps /viewform for /viewform?embedded=true.
 */
export function buildEmbedUrl(productName) {
  const base = googleForm.viewformUrl.replace(/\?.*$/, "");
  const params = new URLSearchParams({ embedded: "true" });
  if (productName && googleForm.productEntryId) {
    params.set(googleForm.productEntryId, productName);
  }
  return `${base}?${params.toString()}`;
}

// ============================================================
// SEO / ANALYTICS
// ============================================================
export const seo = {
  defaultTitle: `${company.brandName} — ${company.tagline}`,
  titleTemplate: `%s | ${company.brandName}`,
  defaultDescription:
    "Rudraksh Corrugation Solutions supplies corrugated packaging, PP bins, protective packaging materials and pallets — tailored to requirement. Greater Noida, India.",
  siteUrl: company.website,
  ogImage: pick("REACT_APP_OG_IMAGE", "/assets/brand/rudraksh-cs-logo.svg"),
  googleSiteVerification: pick("REACT_APP_GOOGLE_SITE_VERIFICATION", ""),
  googleAnalyticsId: pick("REACT_APP_GOOGLE_ANALYTICS_ID", ""),
};

// ============================================================
// NAVIGATION (edit labels or add pages here)
// ============================================================
export const nav = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Products", to: "/products" },
  { label: "Custom Packaging", to: "/custom-packaging" },
  { label: "Industries", to: "/industries" },
  { label: "Clients", to: "/clients" },
  { label: "Contact", to: "/contact" },
];

// Convenience helper for a WhatsApp click-to-chat URL. Returns empty when unset.
export function whatsappUrl(prefilledText = "") {
  if (!company.whatsappNumber) return "";
  const q = prefilledText ? `?text=${encodeURIComponent(prefilledText)}` : "";
  return `https://wa.me/${company.whatsappNumber}${q}`;
}
